import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../services/apiService';
import Loading from '../components/Loading';
import GraficoCustos from './GraficoCustos';
import GraficoNos from './GraficoNos';
import './CustosAtendimento.css';

// Períodos oferecidos. O endpoint aceita 1..365; a janela vale sobre a data DAS ANÁLISES
// (as linhas transcriptions_*), não da gravação — reprocessamento de gravação antiga conta.
const PERIODOS = [7, 30, 90];

// Meta de custo: R$ 1,00 por HORA de atendimento (não por atendimento). É a métrica
// comparável, e é a única em que a meta pode ser desenhada como uma reta.
const META_POR_HORA = 1;

// Marcos que explicam por que a série muda de forma. Datas apuradas no banco e nos runbooks
// em 24/08/2026 — sem eles, um degrau no gráfico parece erro de medição.
// A parcela de infra é SEMPRE do próprio ambiente: a API consulta o Cost Explorer com
// `env=APP_ENV` e, na estimativa, aplica a fatia medida daquele ambiente. Logo um marco de
// OUTRO ambiente não explica nada aqui — pior, convida a atribuir a produção um degrau que
// nasceu em homologação. Cada marco declara em `envs` onde vale; sem `envs`, vale em todos.
//
// A data é ISO porque ela é usada para CASAR com o bucket do gráfico, não só para exibir —
// comparação de string em ISO ordena certo, `dd/mm/aa` não.
const MARCOS = [
    { data: '2026-04-26', texto: 'primeiro atendimento com custo consolidado' },
    { data: '2026-06-29', texto: 'tokens passam a ser gravados por etapa' },
    { data: '2026-07-02', texto: 'objections entra na medição' },
    { data: '2026-08-10', texto: 'objections, products e lost_sales vão para claude-sonnet-5' },
    { data: '2026-08-19', texto: 'este ambiente foi criado', envs: ['hmg'] },
    { data: '2026-08-23', texto: 'Redação por Contexto entra no pipeline', envs: ['prod'] },
    { data: '2026-08-24', texto: 'infra passa a ser atribuível por ambiente (tag Env)' },
    { data: '2026-08-25', envs: ['prod'],
      texto: 'o 4- Eval passa a contar os tokens do Claude; o histórico da raviera foi '
           + 'recomposto até 29/07, então meses antigos subiram' },
];

// Marcos do ambiente que a própria resposta declara. Sem `env` conhecido (resposta antiga
// ou infra fora do ar), mostra só os que valem em todo lugar — nunca os de outro ambiente.
//
// O número é atribuído AQUI, sobre a lista já filtrada, e é o que amarra o marcador do
// gráfico à linha da lista. Numerar antes de filtrar deixaria buracos na sequência.
const marcosDoAmbiente = (env) =>
    MARCOS
        .filter((m) => !m.envs || (env && m.envs.includes(env)))
        .map((m, i) => ({ ...m, n: i + 1 }));

// USAGE_TYPE da AWS é ilegível para quem não vive na fatura: `SAE1-Multi-AZUsage:db.t4g.
// medium` não diz "banco de dados". O prefixo de região sai (não é informação útil aqui) e
// o resto vira nome de recurso. Tipo não mapeado aparece CRU, de propósito: melhor um rótulo
// feio e verdadeiro que um "Outros" que esconde de onde vem o dinheiro.
const RECURSO = [
    [/Multi-AZUsage:(\S+)/, (m) => `Banco RDS ${m[1]} (Multi-AZ)`],
    [/InstanceUsage:(\S+)/, (m) => `Banco RDS ${m[1]}`],
    [/BoxUsage:(\S+)/, (m) => `Servidor EC2 ${m[1]}`],
    [/NodeUsage:(\S+)/, (m) => `Cache ${m[1]}`],
    [/LoadBalancerUsage/, () => 'Balanceador de carga'],
    [/PublicIPv4:InUseAddress/, () => 'IP público'],
    [/EBS:VolumeUsage\.(\w+)/, (m) => `Disco EBS ${m[1]} (provisionado)`],
    [/RDS:Multi-AZ-(\w+)-Storage/, (m) => `Disco do banco ${m[1]} (Multi-AZ, provisionado)`],
    [/RDS:(\w+)-Storage/, (m) => `Disco do banco ${m[1]} (provisionado)`],
    [/BundleUsage:(\S+)/, (m) => `Lightsail ${m[1]}`],
    [/ContainerSvcUsage:(\S+)/, (m) => `Contêiner Lightsail ${m[1]}`],
    [/RuleV2|WebACL/, () => 'Regras de WAF'],
    [/Secret/, () => 'Secrets Manager'],
    [/NatGateway-Hours/, () => 'NAT gateway'],
];

const nomeDoRecurso = (usageType) => {
    const semRegiao = (usageType || '').replace(/^[A-Z]{2}[A-Z0-9]\d-/, '');
    for (const [rx, fmt] of RECURSO) {
        const m = rx.exec(semRegiao);
        if (m) return fmt(m);
    }
    return semRegiao || usageType;
};

const dataCurta = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    return m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : iso;
};
const QUANTIDADES = [5, 10, 25];

// Câmbio digitado à mão fica só neste navegador: é preferência de leitura, não dado do sistema.
const LS_TAXA = 'custosAtendimento_usdBrl';

const lsGet = () => {
    try { return localStorage.getItem(LS_TAXA) || ''; } catch { return ''; }
};

const lsSet = (v) => {
    try {
        if (v) localStorage.setItem(LS_TAXA, v);
        else localStorage.removeItem(LS_TAXA);
    } catch { /* modo privado / storage bloqueado */ }
};

const num = (v, dec = 0) =>
    v == null ? '—' : v.toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec });

const usd = (v, dec = 4) => (v == null ? '—' : `US$ ${v.toFixed(dec)}`);

// Valor que já está em reais (a meta). Não passa por brl(), que converte de dólar.
const emReais = (v, dec = 2) =>
    `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;

const brl = (v, taxa, dec = 2) =>
    v == null || !taxa ? '—' : `R$ ${(v * taxa).toLocaleString('pt-BR', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;

// "2026-08-21 13:07:28.20125" (PTAX) / "2026-08-24T12:41:07" (ISO) -> "24/08 12:41"
const quando = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(iso || '');
    return m ? `${m[3]}/${m[2]} ${m[4]}:${m[5]}` : '—';
};

function CustosAtendimento() {
    // ── Visão agregada (7/30/90 dias) ─────────────────────────────────────────────────
    const [dias, setDias] = useState(30);
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(true);
    // A tela anterior (card na Home) escondia a seção inteira quando o fetch falhava, o que
    // fazia um erro de servidor parecer "não há dados". Aqui o erro aparece.
    const [erro, setErro] = useState('');

    // ── Série temporal (gráfico de tendência) ─────────────────────────────────────────
    const [serie, setSerie] = useState(null);
    const [erroSerie, setErroSerie] = useState('');
    const [modoGrafico, setModoGrafico] = useState('unitario');
    const [verTabela, setVerTabela] = useState(false);
    // Série mensal: independente do seletor de 7/30/90 — o mês é a granularidade em que o
    // custo por hora fica estável o suficiente para comparar. Carrega uma vez.
    const [serieMes, setSerieMes] = useState(null);
    const [erroSerieMes, setErroSerieMes] = useState('');
    const [verTabelaMes, setVerTabelaMes] = useState(false);
    const [verTabelaNos, setVerTabelaNos] = useState(false);
    const [metricaNos, setMetricaNos] = useState('hora');

    // ── Últimos N atendimentos, um por um ─────────────────────────────────────────────
    const [quantos, setQuantos] = useState(10);
    const [empresa, setEmpresa] = useState('');
    const [recentes, setRecentes] = useState(null);
    const [carregandoRec, setCarregandoRec] = useState(true);
    const [erroRec, setErroRec] = useState('');
    const [aberto, setAberto] = useState(null);   // transcription_id expandido

    // String, não número: o campo precisa aceitar "5," no meio da digitação.
    const [campoTaxa, setCampoTaxa] = useState(lsGet);

    const carregar = useCallback(async (d) => {
        setCarregando(true);
        setErro('');
        try {
            const r = await apiService.adminGetAiCostSummary(d);
            if (!r || !Array.isArray(r.models)) throw new Error('Resposta inesperada do servidor');
            setDados(r);
        } catch (err) {
            setDados(null);
            setErro(err.message || 'Erro ao carregar o custo de IA');
        } finally {
            setCarregando(false);
        }
    }, []);

    const carregarRecentes = useCallback(async (n, sd) => {
        setCarregandoRec(true);
        setErroRec('');
        try {
            const r = await apiService.adminGetAiCostRecent(n, sd || null);
            if (!r || !Array.isArray(r.items)) throw new Error('Resposta inesperada do servidor');
            setRecentes(r);
        } catch (err) {
            setRecentes(null);
            setErroRec(err.message || 'Erro ao carregar os últimos atendimentos');
        } finally {
            setCarregandoRec(false);
        }
    }, []);

    const carregarSerie = useCallback(async (d) => {
        setErroSerie('');
        try {
            const r = await apiService.adminGetAiCostSeries(d);
            if (!r || !Array.isArray(r.points)) throw new Error('Resposta inesperada do servidor');
            setSerie(r);
        } catch (err) {
            setSerie(null);
            setErroSerie(err.message || 'Erro ao carregar a série de custo');
        }
    }, []);

    const carregarSerieMes = useCallback(async () => {
        setErroSerieMes('');
        try {
            const r = await apiService.adminGetAiCostSeries(360, null, 'month');
            if (!r || !Array.isArray(r.points)) throw new Error('Resposta inesperada do servidor');
            setSerieMes(r);
        } catch (err) {
            setSerieMes(null);
            setErroSerieMes(err.message || 'Erro ao carregar a evolução mensal');
        }
    }, []);

    useEffect(() => { carregar(dias); }, [dias, carregar]);
    useEffect(() => { carregarSerie(dias); }, [dias, carregarSerie]);
    useEffect(() => { carregarSerieMes(); }, [carregarSerieMes]);
    useEffect(() => { carregarRecentes(quantos, empresa); }, [quantos, empresa, carregarRecentes]);

    const cotacao = dados?.usd_brl || recentes?.usd_brl || null;

    // Adota a cotação da API UMA vez, quando não havia taxa salva. O ref existe para que
    // apagar o campo para redigitar não faça o valor voltar sozinho no meio da digitação.
    const adotouCotacao = useRef(false);
    useEffect(() => {
        if (adotouCotacao.current || campoTaxa || !cotacao?.rate) return;
        adotouCotacao.current = true;
        setCampoTaxa(String(cotacao.rate));
    }, [cotacao, campoTaxa]);

    const taxa = (() => {
        const n = parseFloat(String(campoTaxa).replace(',', '.'));
        return Number.isFinite(n) && n > 0 ? n : null;
    })();

    const usandoCotacao = !!cotacao?.rate && taxa === cotacao.rate;
    const fonteCurta = cotacao?.source ? cotacao.source.split(' ')[0] : '';

    const aplicarCotacao = () => {
        if (!cotacao?.rate) return;
        lsSet('');                       // volta a seguir a cotação a cada carregamento
        setCampoTaxa(String(cotacao.rate));
    };

    const o = dados?.overall;
    // A infra tem TRÊS naturezas e só uma responde a demanda. `variable_usd` é a medida por
    // uso — requisição, transferência, LCU, log ingerido. `fixed_usd` é capacidade
    // contratada e `archive_usd` é o acervo guardado: nenhum dos dois muda porque entrou um
    // atendimento, então nenhum dos dois pertence ao custo de um atendimento novo.
    // `demand_*` só existe na API nova. Sem ela, cai no total cheio e mantém o rótulo
    // antigo — melhor mostrar o número de antes que desenhar zero e mentir por omissão.
    const temDemanda = o?.demand_cost_per_hour_usd != null;
    const porHora = temDemanda ? o.demand_cost_per_hour_usd : o?.cost_per_hour_usd;
    const porAtendimento = temDemanda ? o.avg_demand_cost_usd : o?.avg_cost_usd;
    const infraUso = dados?.infra?.variable_usd ?? null;
    const infraFixa = dados?.infra?.fixed_usd ?? null;
    const infraAcervo = dados?.infra?.archive_usd ?? null;
    // Só os itens de capacidade, do maior para o menor: é a lista que responde "de que a
    // conta fixa é feita", que é a pergunta prática de quem vai enxugar.
    const itensFixos = (dados?.infra?.by_usage_type || [])
        .filter((t) => t.nature === 'fixo' && t.usd > 0)
        .slice(0, 10);
    const ag = recentes?.aggregate;
    // Infra por atendimento é rateio por hora de áudio, não medição — a coluna só aparece
    // quando há taxa, e o texto de ajuda diz o que ela é.
    const temInfraRec = (recentes?.infra?.usd_per_hour || 0) > 0;

    return (
        <div className="ca-page">
            <h1 className="ca-title">💰 Custos de Atendimento</h1>
            <p className="ca-help">
                Custo do atendimento no que <strong>responde a demanda</strong>:
                <strong> LLM</strong>, <strong>transcrição</strong> e a parcela de infra
                <strong> medida por uso</strong>. A métrica que se gerencia é o
                <strong> custo por hora</strong> — a única comparável entre clientes e entre
                meses. A <strong>capacidade</strong> de infra (~93% da conta: hora de
                instância, banco, storage provisionado) tem bloco próprio e fica fora daqui e
                dos gráficos: ela não muda porque entrou um atendimento.
            </p>

            <div className="ca-regra">
                <div className="ca-regra-linha">
                    <strong>Transcrição</strong> — ElevenLabs Scribe v2, por hora de áudio:
                    US$ {(dados?.stt_rates?.base_usd_per_hour ?? 0.22).toFixed(2)} base
                    {' + '}US$ {(dados?.stt_rates?.entity_usd_per_hour ?? 0.07).toFixed(2)} detecção de PII
                    {' + '}US$ {(dados?.stt_rates?.keyterms_usd_per_hour ?? 0.05).toFixed(2)} keyterms
                    (só clientes com termos ativos). Linear na duração.
                </div>
                <div className="ca-regra-linha">
                    <strong>LLM</strong> — tokens de cada etapa ao preço do modelo: input sem cache
                    × preço, cache lido × 10% do preço, cache escrito × 2 (TTL de 1 h), output ×
                    preço de saída.
                </div>
                <div className="ca-regra-linha">
                    <strong>Infra</strong> — uso <em>bruto</em> da conta AWS pelo Cost Explorer
                    (bruto, não desembolso: créditos zeram a fatura hoje e expiram), atribuído por
                    tag <code>Env</code>, sem <code>awon</code> nem <code>lab</code>. Custo
                    <strong> fixo</strong>: por hora, cai quando o volume sobe.
                    {dados?.infra?.scope && (
                        <> Escopo: <strong>{dados.infra.scope}</strong>.</>
                    )}
                </div>
                <div className="ca-regra-linha">
                    <strong>Por hora</strong> — custo total ÷ horas de áudio do período. Vale no
                    agregado, <em>não</em> por atendimento: o LLM tem parte fixa (prompts, schemas,
                    output), então dividir a gravação curta pela sua duração infla o valor.
                </div>
            </div>

            {/* ── Barra de câmbio, comum às duas visões ───────────────────────────────── */}
            <div className="ca-filtros">
                <span className="ca-filtros-label">Dólar</span>
                <div className="ca-cambio">
                    <span className="ca-cambio-prefixo">R$</span>
                    <input
                        className="ca-cambio-input"
                        type="text"
                        inputMode="decimal"
                        value={campoTaxa}
                        placeholder="5,00"
                        onChange={(e) => { setCampoTaxa(e.target.value); lsSet(e.target.value); }}
                        aria-label="Cotação do dólar em reais"
                    />
                </div>
                {cotacao?.rate && (
                    <button
                        className="ca-cotacao"
                        onClick={aplicarCotacao}
                        disabled={usandoCotacao}
                        title={`${cotacao.source} — ${quando(cotacao.quoted_at)}`}
                    >
                        {usandoCotacao
                            ? `cotação ${fonteCurta} de ${quando(cotacao.quoted_at)}`
                            : `usar ${num(cotacao.rate, 4)} (${fonteCurta})`}
                    </button>
                )}
                {(dados || recentes) && !cotacao && (
                    <span className="ca-cambio-aviso">cotação indisponível — informe a taxa</span>
                )}
                {dados?.stt_rates && (
                    <span className="ca-nota">
                        transcrição: US$ {dados.stt_rates.base_usd_per_hour.toFixed(2)}/h
                        {' + '}{dados.stt_rates.entity_usd_per_hour.toFixed(2)} PII
                        {' + '}{dados.stt_rates.keyterms_usd_per_hour.toFixed(2)} keyterms
                    </span>
                )}
            </div>

            {/* ══ Visão agregada ══════════════════════════════════════════════════════ */}
            <h2 className="ca-subtitulo">Período</h2>

            <div className="ca-filtros">
                <span className="ca-filtros-label">Últimos</span>
                {PERIODOS.map((d) => (
                    <button
                        key={d}
                        className={`ca-periodo ${dias === d ? 'ca-periodo-ativo' : ''}`}
                        onClick={() => setDias(d)}
                    >
                        {d} dias
                    </button>
                ))}
                <button className="ca-recarregar" onClick={() => { carregar(dias); carregarSerie(dias); }}>Atualizar</button>
            </div>

            {erro && <div className="ca-erro">{erro}</div>}
            {carregando && <Loading />}

            {!carregando && !erro && o && !o.infra_available && (
                <div className="ca-aviso">
                    <strong>Infra fora da conta.</strong> O Cost Explorer respondeu{' '}
                    <code>{dados?.infra?.error || 'sem dados'}</code>
                    {dados?.infra?.hint ? ` — ${dados.infra.hint}` : ''}. Os valores abaixo são
                    só IA (transcrição + LLM).
                </div>
            )}

            {!carregando && !erro && o && (
                <>
                    <div className="ca-metricas">
                        {/* O número de destaque é o que VARIA COM A DEMANDA: LLM +
                            transcrição + infra medida por uso. Capacidade contratada e acervo
                            ficam fora porque não mudam quando entra um atendimento — somá-los
                            aqui dava um valor que não serve para decidir preço, margem nem
                            meta. O total cheio continua visível, no bloco de capacidade. */}
                        <div className="ca-metrica ca-metrica-destaque">
                            <span className="ca-valor">{brl(porHora, taxa)}</span>
                            <span className="ca-secundario">
                                {porHora != null && taxa
                                    ? `${(porHora * taxa / META_POR_HORA).toFixed(1)}× a meta de ${emReais(META_POR_HORA)}`
                                    : usd(porHora)}
                            </span>
                            <span className="ca-rotulo">
                                {temDemanda ? 'varia com a demanda / hora' : 'total / hora de atendimento'}
                            </span>
                        </div>
                        <div className="ca-metrica ca-metrica-destaque">
                            <span className="ca-valor">{brl(porAtendimento, taxa)}</span>
                            <span className="ca-secundario">{usd(porAtendimento)}</span>
                            <span className="ca-rotulo">
                                {temDemanda ? 'varia com a demanda / atendimento' : 'total / atendimento'}
                            </span>
                        </div>
                        <div className="ca-metrica">
                            <span className="ca-valor">{brl(o.avg_llm_cost_usd, taxa)}</span>
                            <span className="ca-secundario">{brl(o.llm_cost_per_hour_usd, taxa)} / hora</span>
                            <span className="ca-rotulo">LLM / atendimento</span>
                        </div>
                        <div className="ca-metrica">
                            <span className="ca-valor">{brl(o.avg_stt_cost_usd, taxa)}</span>
                            <span className="ca-secundario">{brl(o.stt_cost_per_hour_usd, taxa)} / hora</span>
                            <span className="ca-rotulo">transcrição / atendimento</span>
                        </div>
                        {o.infra_available && infraUso != null && (
                            <div className="ca-metrica">
                                <span className="ca-valor">{brl(infraUso / o.transcriptions, taxa)}</span>
                                <span className="ca-secundario">
                                    {brl(infraUso / o.total_hours, taxa)} / hora
                                    {dados?.infra?.source_kind === 'semente' ? ' · estimado' : ''}
                                    {dados?.infra?.source_kind === 'misto' ? ' · parte estimada' : ''}
                                </span>
                                <span className="ca-rotulo">
                                    infra por uso / atendimento
                                    {dados?.infra?.env ? ` · ${dados.infra.env}` : ''}
                                </span>
                            </div>
                        )}
                        <div className="ca-metrica">
                            <span className="ca-valor">
                                {o.avg_duration_min == null ? '—' : `${num(o.avg_duration_min, 1)} min`}
                            </span>
                            <span className="ca-rotulo">duração média</span>
                        </div>
                        <div className="ca-metrica">
                            <span className="ca-valor">{num(o.transcriptions)}</span>
                            <span className="ca-secundario">{num(o.total_hours)} h de áudio</span>
                            <span className="ca-rotulo">atendimentos analisados</span>
                        </div>
                        <div className="ca-metrica">
                            <span className="ca-valor">{num(o.avg_tokens)}</span>
                            <span className="ca-secundario">{num(o.avg_llm_calls, 1)} chamadas</span>
                            <span className="ca-rotulo">tokens médios</span>
                        </div>
                        <div className="ca-metrica">
                            <span className="ca-valor">{o.cached_pct == null ? '—' : `${o.cached_pct}%`}</span>
                            <span className="ca-rotulo">input em cache</span>
                        </div>
                    </div>

                    <div className="ca-total-periodo">
                        <span><strong>{brl(o.total_cost_usd, taxa)}</strong> no período</span>
                        <span className="ca-nota">
                            LLM {brl(o.llm_cost_usd, taxa)} · transcrição {brl(o.stt_cost_usd, taxa)}
                            {o.infra_available ? ` · infra ${brl(o.infra_cost_usd, taxa)}` : ''}
                            {' · '}{usd(o.total_cost_usd, 2)}
                        </span>
                    </div>

                    {/* Capacidade fica FORA do custo por atendimento e fora dos gráficos, num
                        bloco próprio: ela não responde a demanda, então somá-la ao custo
                        marginal produz um número que não serve para decidir nada. Mas ela
                        existe e é a maior parcela — esconder seria pior que misturar. */}
                    {o.infra_available && infraFixa != null && (
                        <div className="ca-capacidade">
                            <div className="ca-capacidade-cab">
                                <span className="ca-capacidade-t">Capacidade de infra — fora do custo por atendimento</span>
                                <span className="ca-capacidade-v">{brl(infraFixa + (infraAcervo || 0), taxa)}</span>
                            </div>
                            {/* A soma explícita, para o número de destaque não parecer que
                                esconde parte da conta: ele exclui capacidade de propósito, e
                                aqui está o que a conta inteira dá. */}
                            {temDemanda && o.total_hours > 0 && (
                                <p className="ca-capacidade-soma">
                                    <b>{brl(porHora, taxa)}</b> que varia com a demanda
                                    {' + '}
                                    <b>{brl((infraFixa + (infraAcervo || 0)) / o.total_hours, taxa)}</b> de capacidade
                                    {' = '}
                                    <b>{brl(o.cost_per_hour_usd, taxa)}</b> de custo cheio por hora
                                </p>
                            )}
                            <div className="ca-capacidade-itens">
                                <span>
                                    <b>{brl(infraFixa, taxa)}</b> capacidade contratada
                                    <i>hora de instância, banco, balanceador, IP e storage
                                       provisionado — roda igual com 1 ou 10 mil horas de áudio</i>
                                </span>
                                {infraAcervo > 0 && (
                                    <span>
                                        <b>{brl(infraAcervo, taxa)}</b> acervo guardado
                                        <i>S3 por byte-hora, snapshot e backup — é a integral da
                                           demanda passada, e só cai com política de retenção</i>
                                    </span>
                                )}
                                {o.total_hours > 0 && (
                                    <span>
                                        <b>{brl((infraFixa + (infraAcervo || 0)) / o.total_hours, taxa)}</b> por hora hoje
                                        <i>não é uma taxa: divide um valor fixo pelas horas do
                                           período, então CAI sozinha quando o volume sobe</i>
                                    </span>
                                )}
                            </div>
                            {itensFixos.length > 0 && (
                                <div className="ca-capacidade-recursos">
                                    <span className="ca-capacidade-t">De que é feita</span>
                                    <ul>
                                        {itensFixos.map((t) => (
                                            <li key={t.usage_type}>
                                                <b>{brl(t.usd, taxa)}</b>
                                                <span>{nomeDoRecurso(t.usage_type)}</span>
                                                <i>{t.usage_type}</i>
                                            </li>
                                        ))}
                                    </ul>
                                    {dados?.infra?.source_kind === 'semente' && (
                                        <p className="ca-capacidade-nota">
                                            Valores rateados pela fatia do ambiente, não medidos
                                            recurso por recurso: a conta AWS é uma só e a tag
                                            <code> Env</code> ainda não cobre a janela inteira.
                                            A proporção entre os itens é real; o valor absoluto
                                            de cada um é a fatia deste ambiente.
                                        </p>
                                    )}
                                </div>
                            )}
                            <p className="ca-capacidade-nota">
                                A pergunta certa para esta parcela não é "quanto custa por
                                atendimento" e sim <strong>quanto da capacidade paga está sendo
                                usada</strong> — é problema de dimensionamento e agenda, não de
                                eficiência de pipeline. Ela é fixa <em>dentro</em> do envelope
                                atual: cresce em degrau quando a capacidade acaba.
                            </p>
                        </div>
                    )}

                    <div className="ca-grafico-cab">
                        <h3 className="ca-subtitulo3">Tendência</h3>
                        <div className="ca-grafico-controles">
                            <button
                                className={`ca-periodo ${modoGrafico === 'unitario' ? 'ca-periodo-ativo' : ''}`}
                                onClick={() => setModoGrafico('unitario')}
                            >por atendimento</button>
                            <button
                                className={`ca-periodo ${modoGrafico === 'absoluto' ? 'ca-periodo-ativo' : ''}`}
                                onClick={() => setModoGrafico('absoluto')}
                            >total do dia</button>
                            <button className="ca-cotacao" onClick={() => setVerTabela((v) => !v)}>
                                {verTabela ? 'ocultar tabela' : 'ver tabela'}
                            </button>
                        </div>
                    </div>
                    <p className="ca-help ca-help-curta">
                        {modoGrafico === 'unitario'
                            ? 'Sobe e desce com a duração das gravações do dia, não com o volume. A meta é por hora — veja o gráfico mensal.'
                            : 'Acompanha a fatura no que é sensível a demanda. Sobe junto com o volume, então não serve para julgar eficiência.'}
                    </p>

                    {serie?.infra?.unattributed_usd > 0 && (
                        <p className="ca-nota">
                            {brl(serie.infra.unattributed_usd, taxa)} de infra fora do gráfico:
                            dias sem atendimento, ou com menos de{' '}
                            {num(serie.infra.min_hours_per_bucket, 0)} h de áudio — pouco para
                            diluir custo fixo.
                        </p>
                    )}
                    {erroSerie && <div className="ca-erro">{erroSerie}</div>}
                    {serie && (
                        <GraficoCustos
                            points={serie.points}
                            granularity={serie.granularity}
                            taxa={taxa}
                            modo={modoGrafico}
                            marcos={marcosDoAmbiente(serie?.infra?.env)}
                        />
                    )}

                    {serie && verTabela && (
                        <div className="ca-tabela-wrap">
                            <table className="ca-tabela ca-tabela-interna">
                                <thead>
                                    <tr>
                                        <th>{serie.granularity === 'week' ? 'Semana de' : 'Dia'}</th>
                                        <th>Atendimentos</th>
                                        <th>Horas</th>
                                        <th>LLM</th>
                                        <th>Transcrição</th>
                                        <th>Total</th>
                                        <th>Total / atendimento</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {serie.points.map((pt) => (
                                        <tr key={pt.bucket}>
                                            <td>{pt.bucket.split('-').reverse().slice(0, 2).join('/')}</td>
                                            <td>{num(pt.transcriptions)}</td>
                                            <td>{num(pt.hours, 1)}</td>
                                            <td>{brl(pt.llm_cost_usd, taxa)}</td>
                                            <td>{brl(pt.stt_cost_usd, taxa)}</td>
                                            <td>{brl(pt.total_cost_usd, taxa)}</td>
                                            <td>{brl(pt.avg_total_usd, taxa)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="ca-grafico-cab">
                        <h3 className="ca-subtitulo3">Evolução mensal — custo por hora de atendimento</h3>
                        <div className="ca-grafico-controles">
                            <button className="ca-cotacao" onClick={() => setVerTabelaMes((v) => !v)}>
                                {verTabelaMes ? 'ocultar tabela' : 'ver tabela'}
                            </button>
                            <button className="ca-cotacao" onClick={carregarSerieMes}>Atualizar</button>
                        </div>
                    </div>
                    <p className="ca-help ca-help-curta">
                        Últimos 12 meses, com a <strong>meta de R$ 1,00/h</strong> marcada.
                        A transcrição é quase uma reta — é o piso, que só cai mexendo no áudio
                        enviado; a variação está no LLM. A coluna e a linha da meta medem a
                        mesma coisa que o número de destaque no topo: <strong>o que varia com
                        a demanda</strong>. A capacidade tem bloco próprio, com a soma que
                        leva de volta ao custo cheio.
                    </p>

                    <div className="ca-marcos">
                        <span className="ca-marcos-t">Marcos</span>
                        <ul>
                            {marcosDoAmbiente(serieMes?.infra?.env).map((m) => (
                                <li key={m.data}>
                                    <span className="ca-marco-n">{m.n}</span>
                                    <b>{dataCurta(m.data)}</b> {m.texto}
                                </li>
                            ))}
                        </ul>
                        {serieMes?.infra?.buckets_without_infra?.length > 0 && (
                            <p className="ca-marcos-nota">
                                {serieMes.infra.buckets_without_infra.length} mês(es) sem a parcela
                                de infra, por terem menos de{' '}
                                {num(serieMes.infra.min_hours_per_bucket, 0)} h de áudio.
                            </p>
                        )}
                    </div>
                    {serieMes?.infra?.source_kind === 'semente' && (
                        <div className="ca-aviso">
                            <strong>A infra deste gráfico é estimada.</strong>{' '}
                            {serieMes.infra.scope} — o total de cada mês é real, vindo do Cost
                            Explorer; só a repartição entre ambientes vem da medição de 24/08.
                            Passa a ser exata quando a tag <code>Env</code> começar a produzir dado
                            no Billing.
                        </div>
                    )}
                    {/* Série pode legitimamente misturar fontes, porque cada bucket é
                        independente: mês novo tem tag, mês antigo não. Dizer isso é
                        obrigatório — sem o aviso o gráfico compara medição com estimativa
                        lado a lado e não avisa qual é qual. */}
                    {serieMes?.infra?.source_kind === 'misto' && (
                        <div className="ca-aviso">
                            <strong>Este gráfico mistura infra medida e estimada.</strong>{' '}
                            {serieMes.infra.scope}. Buckets anteriores à ativação não têm tag e
                            nunca terão — a ativação não retroage —, então a comparação entre um
                            mês novo e um antigo carrega essa diferença de origem.
                        </div>
                    )}
                    {serieMes?.infra?.source_kind === 'sem_dado' && (
                        <div className="ca-aviso">
                            <strong>Este gráfico está só com IA.</strong> Não há infra por mês para
                            este ambiente: a tag <code>Env</code> ainda não produz dado no Billing e
                            não há fatia medida para ele.
                        </div>
                    )}
                    {erroSerieMes && <div className="ca-erro">{erroSerieMes}</div>}
                    {serieMes && (
                        <GraficoCustos
                            points={serieMes.points}
                            granularity={serieMes.granularity}
                            taxa={taxa}
                            modo="hora"
                            meta={META_POR_HORA}
                            tendencia
                            marcos={marcosDoAmbiente(serieMes?.infra?.env)}
                        />
                    )}

                    {serieMes && verTabelaMes && (
                        <div className="ca-tabela-wrap">
                            <table className="ca-tabela ca-tabela-interna">
                                <thead>
                                    <tr>
                                        <th>Mês</th>
                                        <th>Atendimentos</th>
                                        <th>Horas</th>
                                        <th>LLM / hora</th>
                                        <th>Transcrição / hora</th>
                                        <th>Total / hora</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {serieMes.points.map((pt) => {
                                        const h = pt.hours || 0;
                                        const porHora = (v) => (h > 0 && v != null ? v / h : null);
                                        return (
                                            <tr key={pt.bucket}>
                                                <td>{pt.bucket.slice(0, 7).split('-').reverse().join('/')}</td>
                                                <td>{num(pt.transcriptions)}</td>
                                                <td>{num(pt.hours, 1)}</td>
                                                <td>{brl(porHora(pt.llm_cost_usd), taxa)}</td>
                                                <td>{brl(porHora(pt.stt_cost_usd), taxa)}</td>
                                                <td>{brl(porHora(pt.total_cost_usd), taxa)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="ca-grafico-cab ca-subtitulo-espacado">
                        <h2 className="ca-subtitulo ca-subtitulo-solto">Por nó do pipeline</h2>
                        <div className="ca-grafico-controles">
                            {[['hora', 'por hora'], ['atendimento', 'por atendimento'], ['total', 'total']].map(
                                ([k, rot]) => (
                                    <button
                                        key={k}
                                        className={`ca-periodo ${metricaNos === k ? 'ca-periodo-ativo' : ''}`}
                                        onClick={() => setMetricaNos(k)}
                                    >{rot}</button>
                                ))}
                            <button className="ca-cotacao" onClick={() => setVerTabelaNos((v) => !v)}>
                                {verTabelaNos ? 'ocultar tabela' : 'ver tabela'}
                            </button>
                        </div>
                    </div>
                    <p className="ca-help ca-help-curta">
                        Um nó por prompt que roda hoje, nomeado pelo slug, agrupado por provedor e
                        ordenado por custo.{' '}
                        {metricaNos === 'hora' && (
                            <>A barra é a <strong>fatia que aquele nó contribui para o custo por
                            hora</strong> — somando tudo dá o LLM por hora, então dá para ler de onde
                            vem a conta que se compara com a meta.</>
                        )}
                        {metricaNos === 'atendimento' && (
                            <>Custo médio por atendimento <strong>em que aquele nó rodou</strong>.
                            A soma das barras <em>não</em> é o custo de um atendimento médio — cada nó
                            tem cobertura diferente —, então esta visão não mostra porcentagem.</>
                        )}
                        {metricaNos === 'total' && (
                            <>Gasto absoluto no período. Mistura preço com volume: um nó caro que roda
                            pouco aparece pequeno aqui.</>
                        )}
                        {' '}A escala é a mesma nas duas facetas — é o que deixa a comparação entre
                        provedores visível. Passe o mouse para tokens, cache e cobertura.
                    </p>

                    <GraficoNos stages={dados.stages} taxa={taxa} metrica={metricaNos} />

                    {verTabelaNos && (
                        <div className="ca-tabela-wrap">
                            <table className="ca-tabela ca-tabela-interna">
                                <thead>
                                    <tr>
                                        <th>Nó (slug)</th>
                                        <th>Modelo</th>
                                        <th>Cobertura</th>
                                        <th>Chamadas</th>
                                        <th>Input s/ cache</th>
                                        <th>Cache lido</th>
                                        <th>Output</th>
                                        <th>Cache</th>
                                        <th>/ atendimento</th>
                                        <th>/ hora</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(dados.stages || []).map((st) => (
                                        <tr key={`${st.slug}-${st.model}`}>
                                            <td>{st.slug}</td>
                                            <td>{st.model}</td>
                                            <td>{st.coverage_pct == null ? '—' : `${st.coverage_pct}%`}</td>
                                            <td>{num(st.avg_calls, 1)}</td>
                                            <td>{num(st.avg_uncached_tokens)}</td>
                                            <td>{num(st.avg_cache_read_tokens)}</td>
                                            <td>{num(st.avg_output_tokens)}</td>
                                            <td>{st.cached_pct ? `${st.cached_pct}%` : 'sem cache'}</td>
                                            <td>{brl(st.avg_cost_usd, taxa, 3)}</td>
                                            <td>{brl(st.usd_per_hour, taxa, 3)}</td>
                                            <td>{brl(st.total_cost_usd, taxa)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <h2 className="ca-subtitulo">LLM por modelo</h2>
                    <div className="ca-tabela-wrap">
                        <table className="ca-tabela">
                            <thead>
                                <tr>
                                    <th>Modelo</th>
                                    <th>Atendimentos</th>
                                    <th>Chamadas</th>
                                    <th>Custo médio</th>
                                    <th>Custo médio (US$)</th>
                                    <th>Tokens médios</th>
                                    <th>Cache</th>
                                    <th>Custo total</th>
                                    <th>Custo total (US$)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dados.models.map((m) => (
                                    <tr key={m.model}>
                                        <td>{m.model}</td>
                                        <td>{num(m.transcriptions)}</td>
                                        <td>{num(m.llm_calls)}</td>
                                        <td>{brl(m.avg_cost_usd, taxa, 4)}</td>
                                        <td>{m.avg_cost_usd == null ? '—' : m.avg_cost_usd.toFixed(4)}</td>
                                        <td>{num(m.avg_tokens)}</td>
                                        <td>{m.cached_pct == null ? '—' : `${m.cached_pct}%`}</td>
                                        <td>{brl(m.total_cost_usd, taxa)}</td>
                                        <td>{m.total_cost_usd.toFixed(2)}</td>
                                    </tr>
                                ))}
                                {dados.models.length === 0 && (
                                    <tr><td colSpan={9} className="ca-vazio">Sem análises no período.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
            {/* ══ Últimos N atendimentos ══════════════════════════════════════════════ */}
            <h2 className="ca-subtitulo ca-subtitulo-espacado">Agora — últimos atendimentos, um por um</h2>
            <p className="ca-help ca-help-curta">
                Para conferir efeito de mudança no pipeline, que a média de dias esconde. Clique na
                linha: o sinal está no <strong>detalhe por etapa</strong>, onde os tokens de input
                caem e o cache lido aparece. Compare atendimentos de duração parecida, e lembre que
                cache leva um atendimento para aquecer.
                {temInfraRec && (
                    <> A coluna <strong>Infra</strong> é rateio por hora
                    ({brl(recentes.infra.usd_per_hour, taxa)}/h), igual para toda gravação de mesma
                    duração — não é medição.</>
                )}
            </p>

            <div className="ca-filtros">
                <span className="ca-filtros-label">Quantos</span>
                {QUANTIDADES.map((n) => (
                    <button
                        key={n}
                        className={`ca-periodo ${quantos === n ? 'ca-periodo-ativo' : ''}`}
                        onClick={() => setQuantos(n)}
                    >
                        {n}
                    </button>
                ))}
                <span className="ca-filtros-sep" />
                <span className="ca-filtros-label">Cliente</span>
                <select className="ca-select" value={empresa} onChange={(e) => setEmpresa(e.target.value)}>
                    <option value="">todos</option>
                    {(recentes?.companies || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {recentes?.in_flight > 0 && (
                    <span className="ca-nota">
                        {recentes.in_flight} ainda no pipeline (sem custo fechado, fora da lista)
                    </span>
                )}
                <button className="ca-recarregar" onClick={() => carregarRecentes(quantos, empresa)}>
                    Atualizar
                </button>
            </div>

            {erroRec && <div className="ca-erro">{erroRec}</div>}
            {carregandoRec && <Loading />}

            {!carregandoRec && !erroRec && recentes && (
                <>
                    <div className="ca-tabela-wrap">
                        <table className="ca-tabela">
                            <thead>
                                <tr>
                                    <th />
                                    <th>Quando</th>
                                    {!empresa && <th>Cliente</th>}
                                    <th>Duração</th>
                                    <th>LLM</th>
                                    <th>Transcrição</th>
                                    {temInfraRec && <th>Infra</th>}
                                    <th>Total</th>
                                    <th>Chamadas</th>
                                    <th>Cache (input)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recentes.items.map((i) => (
                                    <React.Fragment key={`${i.subdomain}-${i.transcription_id}`}>
                                        <tr
                                            className={`ca-linha-clicavel ${aberto === i.transcription_id ? 'ca-linha-aberta' : ''}`}
                                            onClick={() => setAberto(aberto === i.transcription_id ? null : i.transcription_id)}
                                        >
                                            <td className="ca-caret">{aberto === i.transcription_id ? '▾' : '▸'}</td>
                                            <td>{quando(i.created)}</td>
                                            {!empresa && <td>{i.subdomain}</td>}
                                            <td>{i.duration_min == null ? '—' : `${num(i.duration_min, 1)} min`}</td>
                                            <td>{brl(i.llm_cost_usd, taxa)}</td>
                                            <td>{brl(i.stt_cost_usd, taxa)}</td>
                                            {temInfraRec && <td>{brl(i.infra_cost_usd, taxa)}</td>}
                                            <td className="ca-forte">{brl(i.total_cost_usd, taxa)}</td>
                                            <td>{num(i.llm_calls)}</td>
                                            <td>{i.cached_pct == null ? '—' : `${i.cached_pct}%`}</td>
                                        </tr>
                                        {aberto === i.transcription_id && (
                                            <tr className="ca-detalhe">
                                                <td />
                                                <td colSpan={(empresa ? 7 : 8) + (temInfraRec ? 1 : 0)}>
                                                    <div className="ca-detalhe-cab">
                                                        {i.subdomain} · loja {i.store_id} · id {i.transcription_id}
                                                        {' · '}transcrição {num(i.duration_min, 1)} min a{' '}
                                                        US$ {(0.22 + 0.07 + (i.keyterms ? 0.05 : 0)).toFixed(2)}/h
                                                        {i.keyterms ? ' (com keyterms)' : ' (sem keyterms)'}
                                                    </div>
                                                    <table className="ca-tabela ca-tabela-interna">
                                                        <thead>
                                                            <tr>
                                                                <th>Etapa</th>
                                                                <th>Modelo</th>
                                                                <th>Chamadas</th>
                                                                <th>Input s/ cache</th>
                                                                <th>Cache lido</th>
                                                                <th>Cache escrito</th>
                                                                <th>Output</th>
                                                                <th>Custo</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(i.stages || []).map((s) => (
                                                                <tr key={`${s.stage}-${s.model}`}>
                                                                    <td>{s.stage}</td>
                                                                    <td>{s.model}</td>
                                                                    <td>{num(s.llm_calls)}</td>
                                                                    <td>{num(s.uncached_tokens)}</td>
                                                                    <td>{num(s.cache_read_tokens)}</td>
                                                                    <td>{num(s.cache_write_tokens)}</td>
                                                                    <td>{num(s.output_tokens)}</td>
                                                                    <td>{brl(s.cost_usd, taxa, 3)}</td>
                                                                </tr>
                                                            ))}
                                                            {(i.stages || []).length === 0 && (
                                                                <tr><td colSpan={8} className="ca-vazio">
                                                                    Nenhuma etapa com usage gravado.
                                                                </td></tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                                {recentes.items.length === 0 && (
                                    <tr><td colSpan={temInfraRec ? 10 : 9} className="ca-vazio">
                                        Nenhum atendimento consolidado.
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {ag && ag.n > 0 && (
                        <div className="ca-resumo-bloco">
                            <span><strong>mediana</strong> {brl(ag.median_cost_usd, taxa)}/atendimento</span>
                            <span><strong>média</strong> {brl(ag.avg_cost_usd, taxa)}/atendimento</span>
                            <span className="ca-nota">
                                com n={ag.n} a mediana engana menos que a média
                            </span>
                        </div>
                    )}
                </>
            )}

        </div>
    );
}

export default CustosAtendimento;
